import { WORK_VIEWS, isWorkView, type WorkView } from "./prospects/prospect-work-views";
import { ProspectFilters, type StatusFilter } from "./prospects/ProspectFilters";
import { ProspectResults } from "./prospects/ProspectResults";
import { ProspectDialog } from "./prospects/ProspectDialog";
import { useEffect, useMemo, useRef, useState } from "react";
import { Btn, I } from "../ui";
import type { DiscardedFilter, ProvinceDTO } from "../../api/contract";
import {
  ApiRequestError,
  backfillProspectImmobili,
  bulkSendProspectsToHerobot,
  discardProspect,
  exportSelectedProspects,
  getNecrologiStats,
  getProspect,
  listProspects,
  prospectsExportUrl,
  restoreProspect,
  setProspectWorked,
  type ProspectDetail,
  type ProspectListItem,
  type ProspectListResponse,
} from "../api";
import { ProspectDetailView } from "./prospects/ProspectDetailView";
import { buildProspectSubtitle } from "./prospects/ProspectListParts";
import { personName } from "../format";
import { useDemoScope } from "../useDemoScope";

const PAGE_SIZE = 50;
const EXPORT_LIMIT = 5000;
const EMPTY_EXPORT_SELECTION: ReadonlySet<number> = new Set();

export function Prospects({ provinces, defaultProvincia = "", initialView }: { provinces: ProvinceDTO[]; defaultProvincia?: string; initialView?: string }) {
  const startView = WORK_VIEWS[isWorkView(initialView) ? initialView : "tutte"];
  const [data, setData] = useState<ProspectListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<StatusFilter>(startView.status);
  const [provincia, setProvincia] = useState(defaultProvincia);
  const [scopeProvinces, setScopeProvinces] = useState<string[]>([]);
  // Nella demo Herobot è spento: niente conteggi "da inviare".
  const demoScope = useDemoScope();
  const [q, setQ] = useState("");
  const [minImmobili, setMinImmobili] = useState<number>(startView.minImmobili);
  const [showFilters, setShowFilters] = useState(false);
  const [soloXml, setSoloXml] = useState(false);
  const [soloCasaVuota, setSoloCasaVuota] = useState(false);
  const [hideWorked, setHideWorked] = useState(false);
  const [discarded, setDiscarded] = useState<DiscardedFilter>("exclude");
  const [offset, setOffset] = useState(0);
  // Incrementato per forzare un rifetch anche quando i filtri/offset non cambiano.
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedId, updateSelectedId] = useState<number | null>(null);
  const selectionRef = useRef({ id: null as number | null, version: 0 });
  const selectionVersion = selectionRef.current.version;
  const [detail, setDetail] = useState<ProspectDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [backfillMsg, setBackfillMsg] = useState<string | null>(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [herobotBusy, setHerobotBusy] = useState(false);
  const [herobotFailures, setHerobotFailures] = useState<Array<{ id: number; error: string | null }>>([]);
  // Azione in corso su una riga (spunta, Scarta, Rimetti) e ultimo scarto annullabile.
  const [rowBusyId, setRowBusyId] = useState<number | null>(null);
  const [rowMsg, setRowMsg] = useState<string | null>(null);
  const [undoDiscard, setUndoDiscard] = useState<{ id: number; name: string } | null>(null);
  const [excelSelection, setExcelSelection] = useState<{ scope: string; ids: Set<number> }>({ scope: "", ids: new Set() });
  const [exportBusy, setExportBusy] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  function setSelectedId(id: number | null) {
    selectionRef.current = { id, version: selectionRef.current.version + 1 };
    updateSelectedId(id);
    setDetail(null);
    setDetailErr(null);
  }

  // Debounce della ricerca: senza, ogni keystroke lanciava subito 3 query SQL
  // (lista + count + stats). Il fetch usa qDebounced, l'input resta su q.
  const [qDebounced, setQDebounced] = useState("");
  const exportFilters = {
    status: status || null, provincia: provincia || null, q: qDebounced.trim() || null,
    minImmobili: minImmobili > 0 ? minImmobili : null, hasDocument: soloXml ? true : null,
    casaVuota: soloCasaVuota ? true : null, discarded, worked: hideWorked ? "exclude" as const : null,
  };
  const exportScope = JSON.stringify(exportFilters);
  const excelIds = excelSelection.scope === exportScope ? excelSelection.ids : EMPTY_EXPORT_SELECTION;
  useEffect(() => {
    setExcelSelection({ scope: exportScope, ids: new Set() });
    setExportErr(null);
  }, [exportScope, data?.canExportExcel]);
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    void getNecrologiStats().then((stats) => {
      if (!cancelled) setScopeProvinces(stats.byProvincia.map((item) => item.provincia));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setOffset(0);
  }, [status, provincia, qDebounced, minImmobili, soloXml, soloCasaVuota, discarded, hideWorked]);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setErr(null);
    listProspects({
      limit: PAGE_SIZE,
      offset,
      status: status || null,
      provincia: provincia || null,
      q: qDebounced.trim() || null,
      minImmobili: minImmobili > 0 ? minImmobili : null,
      hasDocument: soloXml ? true : null,
      casaVuota: soloCasaVuota ? true : null,
      discarded,
      worked: hideWorked ? "exclude" : null,
    })
      .then((res) => {
        if (cancelled) return;
        const lastOffset = Math.max(0, Math.ceil(res.total / PAGE_SIZE) - 1) * PAGE_SIZE;
        if (offset > lastOffset) {
          setOffset(lastOffset);
          return;
        }
        setData(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(e instanceof ApiRequestError ? e.message : "Errore caricamento");
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [status, provincia, qDebounced, minImmobili, soloXml, soloCasaVuota, discarded, hideWorked, offset, reloadKey]);

  useEffect(() => {
    if (selectedId === null) {
      setDetail(null);
      setDetailErr(null);
      return;
    }
    let cancelled = false;
    setDetailBusy(true);
    setDetailErr(null);
    getProspect(selectedId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setDetailErr(e instanceof ApiRequestError ? e.message : "Errore caricamento dettaglio");
      })
      .finally(() => {
        if (!cancelled) setDetailBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const provOptions = useMemo(() => {
    const codes = new Set(scopeProvinces);
    const options: Pick<ProvinceDTO, "code" | "name">[] = provinces.filter((p) => !codes.size || codes.has(p.code));
    const names: Record<string, string> = {
      FG: "Foggia", VA: "Varese", NO: "Novara", BG: "Bergamo", PV: "Pavia", VE: "Venezia", CO: "Como",
      VB: "Verbano-Cusio-Ossola", PN: "Pordenone", RO: "Rovigo", UD: "Udine", TS: "Trieste", LI: "Livorno",
    };
    for (const code of new Set([...codes, ...(data?.items.map((item) => item.provincia) ?? []), defaultProvincia, provincia])) {
      if (code && !options.some((p) => p.code === code)) options.push({ code, name: names[code] ?? code });
    }
    return options;
  }, [provinces, scopeProvinces, data, defaultProvincia, provincia]);
  const total = data?.total ?? 0;
  const stats = data?.stats;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  function refresh() {
    setOffset(0);
    setReloadKey((k) => k + 1);
  }

  function toggleExcelSelection(ids: number[], checked: boolean) {
    if (busy || !data?.canExportExcel) return;
    if (checked && new Set([...excelIds, ...ids]).size > EXPORT_LIMIT) {
      setExportErr(`Puoi selezionare al massimo ${EXPORT_LIMIT.toLocaleString("it-IT")} schede per esportazione.`);
      return;
    }
    setExportErr(null);
    setExcelSelection(previous => {
      const next = new Set(previous.scope === exportScope ? previous.ids : []);
      for (const id of ids) checked ? next.add(id) : next.delete(id);
      return next.size <= EXPORT_LIMIT ? { scope: exportScope, ids: next } : previous;
    });
  }

  function removeExcelSelection(id: number) {
    setExcelSelection(previous => {
      const ids = new Set(previous.ids);
      ids.delete(id);
      return { ...previous, ids };
    });
  }

  async function runSelectedExport() {
    if (!excelIds.size || exportBusy || busy || !data?.canExportExcel) return;
    setExportBusy(true);
    setExportErr(null);
    let objectUrl: string | undefined;
    try {
      const blob = await exportSelectedProspects([...excelIds], exportFilters);
      objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `prospect-selezionati-${new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Rome" })}.xlsx`;
      document.body.appendChild(link);
      try { link.click(); } finally { link.remove(); }
    } catch (error) {
      setExportErr(error instanceof ApiRequestError ? error.message : "Esportazione non riuscita. Riprova.");
    } finally {
      if (objectUrl) { const url = objectUrl; setTimeout(() => URL.revokeObjectURL(url), 1000); }
      setExportBusy(false);
    }
  }

  /** Aggiorna una scheda nella lista e nel dettaglio aperto; `remove` la toglie dalla pagina. */
  function patchProspect(id: number, patch: Partial<ProspectListItem>, remove = false) {
    if (remove) removeExcelSelection(id);
    setData((prev) => {
      if (!prev) return prev;
      const items = remove
        ? prev.items.filter((p) => p.id !== id)
        : prev.items.map((p) => (p.id === id ? { ...p, ...patch } : p));
      return { ...prev, items, total: remove && items.length < prev.items.length ? prev.total - 1 : prev.total };
    });
    setDetail((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  }

  async function runRowAction(id: number, action: () => Promise<void>, failure: string) {
    setRowBusyId(id);
    setRowMsg(null);
    try {
      await action();
      // Ricarica in silenzio conteggi e pagina: la riga resta dov'è finché non arrivano i dati nuovi.
      setReloadKey((k) => k + 1);
    } catch (e) {
      setRowMsg(e instanceof ApiRequestError ? e.message : failure);
    } finally {
      setRowBusyId(null);
    }
  }

  function toggleWorked(id: number, worked: boolean) {
    return runRowAction(id, async () => {
      const res = await setProspectWorked(id, worked);
      patchProspect(id, { workedAt: res.workedAt, workedBy: res.workedBy }, hideWorked && worked);
    }, "Non sono riuscito a salvare la spunta");
  }

  function discard(id: number, name: string) {
    const actionSelectionVersion = selectionRef.current.version;
    return runRowAction(id, async () => {
      const next = await discardProspect(id);
      const hidden = discarded === "exclude";
      if (hidden && selectionRef.current.id === id
        && selectionRef.current.version === actionSelectionVersion) setSelectedId(null);
      patchProspect(id, { discardedAt: next.discardedAt, discardedBy: next.discardedBy }, hidden);
      setUndoDiscard({ id, name });
    }, "Scarto non riuscito");
  }

  function restore(id: number) {
    const actionSelectionVersion = selectionRef.current.version;
    return runRowAction(id, async () => {
      const next = await restoreProspect(id);
      const hidden = discarded === "only";
      if (hidden && selectionRef.current.id === id
        && selectionRef.current.version === actionSelectionVersion) setSelectedId(null);
      patchProspect(id, { discardedAt: next.discardedAt, discardedBy: next.discardedBy }, hidden);
      setUndoDiscard((u) => (u?.id === id ? null : u));
    }, "Ripristino non riuscito");
  }

  const rowActions = {
    onToggleWorked: (p: ProspectListItem) => void toggleWorked(p.id, p.workedAt == null),
    onDiscard: (p: ProspectListItem) => void discard(p.id, personName(p.nome)),
    onRestore: (p: ProspectListItem) => void restore(p.id),
  };

  async function runBackfill() {
    setBackfillBusy(true);
    setBackfillMsg(null);
    try {
      const res = await backfillProspectImmobili();
      setBackfillMsg(
        `Dati aggiornati: ${res.updated} su ${res.scanned} visure rilette.`,
      );
      refresh();
    } catch (e) {
      setBackfillMsg(e instanceof ApiRequestError ? e.message : "Errore nel recupero dati visura");
    } finally {
      setBackfillBusy(false);
    }
  }

  async function runHerobotBulk() {
    if (!herobotConfigured) {
      setBackfillMsg("Herobot non è configurato per questa agenzia. Contatta l’amministratore per attivarlo.");
      return;
    }
    const ready = stats?.herobotReady ?? 0;
    const batch = Math.min(10, ready);
    if (batch < 1) {
      setBackfillMsg("Nessuna persona pronta da inviare a Herobot: serve una visura letta con immobili.");
      return;
    }
    if (!confirm(`Inviare a Herobot le prossime ${batch} persone con immobili non ancora inviate?`)) {
      return;
    }
    setHerobotBusy(true);
    setBackfillMsg(null);
    setHerobotFailures([]);
    try {
      const res = await bulkSendProspectsToHerobot({ limit: batch });
      setBackfillMsg(
        `Herobot: ${res.sent} inviate, ${res.failed} non riuscite su ${res.scanned}.`,
      );
      setHerobotFailures(res.results.filter((item) => !item.ok));
      refresh();
    } catch (e) {
      setBackfillMsg(e instanceof ApiRequestError ? e.message : "Errore invio Herobot");
    } finally {
      setHerobotBusy(false);
    }
  }

  const activeView = (Object.keys(WORK_VIEWS) as WorkView[]).find(
    (id) => WORK_VIEWS[id].status === status && WORK_VIEWS[id].minImmobili === minImmobili,
  );
  function selectView(id: WorkView) {
    setStatus(WORK_VIEWS[id].status);
    setMinImmobili(WORK_VIEWS[id].minImmobili);
    setOffset(0);
  }
  const herobotReady = stats?.herobotReady ?? 0;
  const herobotConfigured = !demoScope && data?.herobot?.configured === true;
  // Invio e recupero riguardano le visure lette: nascosti mentre si controllano gli omonimi.
  const showSendActions = activeView === "con_immobili" || activeView === "tutte" || activeView === undefined;
  const views: Array<{ id: WorkView; label: string; count: number | null; hint: string }> = [
    { id: "da_controllare", label: "Da controllare", count: stats?.omonimiFound ?? null, hint: "Più persone con lo stesso nome: scegli quella giusta" },
    {
      id: "con_immobili",
      label: "Con immobili",
      count: stats?.visuraOk ?? null,
      hint: demoScope
        ? "Visure lette: apri la scheda per vedere gli immobili"
        : !herobotConfigured ? "Herobot non disponibile per questa agenzia"
        : herobotReady > 0 ? `${herobotReady} da inviare a Herobot` : "Tutte già inviate a Herobot",
    },
    { id: "nessuna", label: "Nessuna corrispondenza", count: stats?.noMatch ?? null, hint: "Nessun immobile trovato a quel nome" },
    { id: "tutte", label: "Tutte le ricerche", count: null, hint: "Ogni esito, compresi errori e ricerche in corso" },
  ];

  return (
    <div className="col gap-16">
      <nav className="work-views" aria-label="Viste di lavoro">
        {views.map((v) => (
          <button key={v.id} type="button" className="work-view" aria-pressed={activeView === v.id} onClick={() => selectView(v.id)}>
            <span className="work-view-label">{v.label}</span>
            {v.count !== null && <span className="work-view-count">{v.count.toLocaleString("it-IT")}</span>}
            <span className="work-view-hint">{v.hint}</span>
          </button>
        ))}
      </nav>

      <div className="prospect-toolbar">
        {showSendActions && herobotConfigured && herobotReady > 0 && (
          <Btn
            variant="primary"
            disabled={herobotBusy || backfillBusy}
            onClick={() => void runHerobotBulk()}
            title="Invia a Herobot fino a 10 persone con immobili non ancora inviate (chiede conferma)"
          >
            {herobotBusy ? "Invio…" : `Invia a Herobot (${Math.min(10, herobotReady)})`}
          </Btn>
        )}
        {showSendActions && (
          <Btn
            disabled={backfillBusy || herobotBusy}
            onClick={() => void runBackfill()}
            title="Rilegge le visure già scaricate e aggiorna gli immobili"
          >
            {backfillBusy ? "Recupero…" : "Recupera dati visura"}
          </Btn>
        )}
        <Btn variant="ghost" icon={I.refresh} onClick={refresh}>Aggiorna</Btn>
        {total > 0 && data?.canExportExcel && (
          <>
            <Btn disabled={!excelIds.size || exportBusy || busy || !!err} onClick={() => void runSelectedExport()} icon={I.download}>
              {exportBusy ? "Esportazione…" : `Esporta selezionati (${excelIds.size.toLocaleString("it-IT")})`}
            </Btn>
            <a
              className="btn ghost"
              href={prospectsExportUrl(exportFilters)}
              download
              title="Scarica in Excel le schede della lista con i filtri attivi, con un foglio per gli immobili"
            >
              {I.download} Esporta tutti in Excel ({Math.min(total, EXPORT_LIMIT).toLocaleString("it-IT")})
            </a>
          </>
        )}
        {exportErr && <span role="alert" style={{ color: "var(--danger)" }}>{exportErr}</span>}
        {backfillMsg && <span className="prospect-toolbar-msg" role="status">{backfillMsg}</span>}
      </div>

      {showSendActions && !demoScope && data?.herobot?.configured === false && (
        <div className="muted" role="status">
          Herobot non è configurato per questa agenzia. Contatta l’amministratore per attivarlo.
        </div>
      )}

      {herobotFailures.length > 0 && (
        <div role="status">
          <strong>Invii Herobot non riusciti</strong>
          <ul>
            {herobotFailures.map((failure) => (
              <li key={failure.id}>
                <Btn size="sm" variant="ghost" onClick={() => setSelectedId(failure.id)}>
                  Scheda #{failure.id}
                </Btn>
                {": "}{failure.error || "Invio non riuscito: apri la scheda e riprova."}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(undoDiscard || rowMsg) && (
        <div className={`prospect-undo${rowMsg ? " is-error" : ""}`} role="status">
          {rowMsg ?? <>
            <span>Scheda di <strong>{undoDiscard!.name}</strong> scartata: non compare più nella lista di lavoro.</span>
            <Btn size="sm" disabled={rowBusyId !== null} onClick={() => void restore(undoDiscard!.id)}>Annulla</Btn>
          </>}
          <button type="button" className="prospect-undo-close" aria-label="Chiudi avviso" onClick={() => { setUndoDiscard(null); setRowMsg(null); }}>{I.x}</button>
        </div>
      )}

      <ProspectFilters
        q={q} setQ={setQ} provincia={provincia} setProvincia={setProvincia} provOptions={provOptions}
        soloCasaVuota={soloCasaVuota} setSoloCasaVuota={setSoloCasaVuota}
        hideWorked={hideWorked} setHideWorked={setHideWorked} workedCount={stats?.workedCount ?? 0}
        showFilters={showFilters} setShowFilters={setShowFilters} status={status} setStatus={setStatus}
        discarded={discarded} setDiscarded={setDiscarded} minImmobili={minImmobili} setMinImmobili={setMinImmobili}
        soloXml={soloXml} setSoloXml={setSoloXml}
      />

      <ProspectResults
        total={total} offset={offset} pageEnd={pageEnd} pageSize={PAGE_SIZE} err={err} data={data} busy={busy}
        selectedId={selectedId} rowBusyId={rowBusyId} rowActions={rowActions} onSelect={setSelectedId}
        canPrev={canPrev} canNext={canNext} setOffset={setOffset}
        exportSelection={data?.canExportExcel ? {
          ids: excelIds, onToggle: toggleExcelSelection, disabled: busy || !!err,
          onClear: () => { setExcelSelection({ scope: exportScope, ids: new Set() }); setExportErr(null); },
        } : undefined}
      />

      <ProspectDialog
        open={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title={detail ? personName(detail.nome) : "Scheda persona"}
        sub={detail ? buildProspectSubtitle(detail) : ""}
        actions={detail && (
          <>
            <button
              type="button"
              className="btn worked-btn"
              aria-pressed={detail.workedAt != null}
              disabled={rowBusyId === detail.id}
              title={detail.workedAt != null ? "Clic per rimetterla da lavorare" : "Spunta la scheda: non dovrai riaprirla al prossimo giro"}
              onClick={() => void toggleWorked(detail.id, detail.workedAt == null)}
            >
              <span className="worked-btn-box" aria-hidden="true">{I.check}</span>
              {detail.workedAt != null ? "Già lavorata" : "Segna come lavorata"}
            </button>
            {detail.discardedAt != null ? (
              <Btn disabled={rowBusyId === detail.id} onClick={() => void restore(detail.id)} title="Rimetti la scheda nella lista di lavoro">Rimetti in lista</Btn>
            ) : (
              <Btn disabled={rowBusyId === detail.id} onClick={() => void discard(detail.id, personName(detail.nome))} title="Togli la scheda dalla lista di lavoro. La ricerca SISTER resta com'è.">Scarta</Btn>
            )}
          </>
        )}
      >
        {detailBusy && <div className="muted" style={{ fontSize: 12 }}>Caricamento…</div>}
        {detailErr && <div style={{ color: "var(--danger)", fontSize: 12 }}>{detailErr}</div>}
        {detail && !detailBusy && (
          <ProspectDetailView
            detail={detail}
            onUpdated={(next) => {
              const selection = selectionRef.current;
              if (selection.id === next.id && selection.version === selectionVersion) {
                setDetail(next);
                if ((discarded === "exclude" && next.discardedAt != null)
                  || (discarded === "only" && next.discardedAt == null)) {
                  setSelectedId(null);
                  removeExcelSelection(next.id);
                  refresh();
                }
              }
              setData((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  items: prev.items.map((p) => (p.id === next.id ? { ...p, ...next } : p)),
                };
              });
              setReloadKey((k) => k + 1);
            }}
          />
        )}
      </ProspectDialog>
    </div>
  );
}
